import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgMore from '@icons/more.svg?react';
import { getOverlayBounds, OverlayPlacement, placeOverlay } from '@utils/contentBounds.ts';
import { Z_LAYERS } from '@styles/zLayers.ts';
import clsx from 'clsx';
import SecuredArea from '@components/security/SecuredArea';
import { isSecuredVisible, RuleInput, useSecuredDecoration } from '@components/security/securedDecoration';

export interface MoreMenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  command: () => void;
  /** Plain gate: the item is dropped when false. Use `secured` when a permission decides it. */
  visible?: boolean;
  /**
   * Rendered but not actionable. Unlike `visible: false` it keeps the item, and therefore the
   * trigger, on screen — so a row whose only action is currently unavailable still shows its menu.
   */
  disabled?: boolean;
  /** Permission gate. Wraps the row in a `SecuredArea`, so security mode can say why it is missing. */
  secured?: RuleInput;
  /** Overrides the SecuredArea id, which defaults to `more-<id>`. */
  securedId?: string;
  dataTestId?: string;
}

export interface MoreMenuSection {
  title?: string;
  items: MoreMenuItem[];
}

interface ComponentProps {
  sections: MoreMenuSection[];
  buttonSize?: 'small' | 'big';
  /** Trigger style. `table` for a row of a data table, the rectangle outline elsewhere. */
  buttonMode?: 'outlined-rectangle' | 'table';
  dataTestId?: string;
}

/** Distance between the trigger and the panel. */
const OFFSET_PX = 6;

interface PanelPosition extends OverlayPlacement {
  right: number;
}

const samePosition = (a: PanelPosition | null, b: PanelPosition) =>
  !!a && a.top === b.top && a.bottom === b.bottom && a.right === b.right && a.maxHeight === b.maxHeight;

/**
 * The three-dots menu: a trigger plus a panel of grouped actions.
 *
 * One implementation for every three-dots menu. Each feature used to carry its own copy, which is
 * how the stacking bug below got fixed in one of them and not the others.
 *
 * The panel is portaled to `<body>` rather than rendered as a child. As a child it lost to any
 * `sticky` strip further down the page: an absolutely positioned panel only outranks what shares its
 * stacking context, and a sticky header with a z-index establishes one of its own — so no z-index on
 * the menu's side reliably wins.
 */
const MoreButton: React.FC<ComponentProps> = ({ sections, buttonSize = 'small', buttonMode = 'outlined-rectangle', dataTestId }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openUpwardsRef = useRef<boolean | null>(null);
  // Called without an id: this asks whether security mode is on, it does not gate the menu.
  const { enabled: securityModeEnabled } = useSecuredDecoration();

  /** A secured item still counts as present while security mode is on, so the mode can reveal it. */
  const isItemPresent = (item: MoreMenuItem) =>
    item.visible !== false && (securityModeEnabled || !item.secured || isSecuredVisible({ id: item.id, visible: item.secured }));

  const visibleSections = sections.map(section => ({ ...section, items: section.items.filter(isItemPresent) })).filter(section => section.items.length > 0);
  const hasItems = visibleSections.length > 0;

  /**
   * Places the panel against the trigger in viewport coordinates, since it is fixed. No measurement is
   * cached: the trigger's own rect read has already flushed layout, so reading the rest costs nothing.
   */
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const rect = trigger?.getBoundingClientRect();
    if (!rect) return;

    // Right-aligned to the trigger, but never past its band: the content column, or the side panel it lives in.
    const bounds = getOverlayBounds(trigger);
    const rightEdge = Math.min(rect.right, bounds.right - OFFSET_PX);
    const right = Math.max(OFFSET_PX, document.documentElement.clientWidth - rightEdge);

    const panel = panelRef.current;
    // scrollHeight survives maxHeight clamping but excludes the border, which maxHeight has to cover.
    const border = panel ? parseFloat(getComputedStyle(panel).borderTopWidth) * 2 : 0;
    const contentHeight = panel ? panel.scrollHeight + border : 0;

    // The side is kept from the first call so that scrolling cannot move the panel under the pointer.
    const placement = placeOverlay(rect, contentHeight, { gap: OFFSET_PX, margin: OFFSET_PX, openUpwards: openUpwardsRef.current ?? undefined });
    if (!placement) {
      setOpen(false);
      return;
    }
    openUpwardsRef.current = placement.bottom !== undefined;

    const next: PanelPosition = { ...placement, right };
    setPosition(current => (samePosition(current, next) ? current : next));
  }, []);

  // Before paint, so the panel is never shown at a stale position for a frame.
  useLayoutEffect(() => {
    if (open && !hasItems) {
      // Rendering nothing would leave `open` set, so the menu returns already open with the items.
      setOpen(false);
      return;
    }
    if (!open) {
      openUpwardsRef.current = null;
      setPosition(null);
      return;
    }
    reposition();
  }, [open, hasItems, reposition]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      // Both have to be checked: the panel is not a descendant of the trigger. Without the panel
      // check, pressing an item would close the menu on mousedown and unmount the row before its
      // click ever landed.
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // A fixed panel does not travel with the page, so it follows the trigger instead. Capture phase,
    // since the scroll usually happens on an inner container whose events never reach the window.
    const onScrollOrResize = () => reposition();

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, { capture: true, passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, { capture: true });
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, reposition]);

  if (!hasItems) return null;

  const renderItem = (item: MoreMenuItem) => {
    const row = (
      <li
        // The last row of a section carries no rule — the section gap or the panel edge separates it.
        className={clsx(
          'flex items-center gap-2 whitespace-nowrap p-4 border-0 border-b border-solid border-content-quaternary last:border-b-0',
          item.disabled ? 'cursor-default text-content-tertiary' : 'cursor-pointer hover:bg-base-quaternary',
        )}
        onClick={() => {
          if (item.disabled) return;
          setOpen(false);
          item.command();
        }}
        data-testid={item.dataTestId}
      >
        {item.icon}
        {item.label}
      </li>
    );
    if (!item.secured) return <React.Fragment key={item.id}>{row}</React.Fragment>;
    return (
      <SecuredArea key={item.id} id={item.securedId ?? `more-${item.id}`} label={item.label} visible={item.secured}>
        {row}
      </SecuredArea>
    );
  };

  const titled = visibleSections.some(section => !!section.title);

  return (
    <div ref={triggerRef} className="inline-flex">
      <RingilButton mode={buttonMode} icon={<SvgMore />} size={buttonSize} onClick={() => setOpen(!open)} dataTestId={dataTestId} />
      {open &&
        createPortal(
          <div
            ref={panelRef}
            // Hidden until measured: the height that decides the side is readable only once mounted.
            style={{
              position: 'fixed',
              top: position?.top,
              bottom: position?.bottom,
              right: position?.right,
              maxHeight: position?.maxHeight,
              visibility: position ? undefined : 'hidden',
              zIndex: Z_LAYERS.popup,
            }}
            className="min-w-[150px] overflow-y-auto rounded-lg border border-solid border-[#ccc] bg-white p-2.5"
          >
            {visibleSections.map((section, index) => (
              <React.Fragment key={index}>
                {section.title && (
                  <div className="mb-2 px-4 py-2 text-[0.9rem] font-bold text-content-primary border-0 border-b border-solid border-content-quaternary">
                    {section.title}
                  </div>
                )}
                {/* Untitled sections are a grouping of the model only, so they read as one list. */}
                <ul className={clsx('list-none p-0 m-0', titled && 'mb-[15px] last:mb-0')}>{section.items.map(renderItem)}</ul>
              </React.Fragment>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default MoreButton;
